/**
 * useTxHistory — persists recent swap transactions to localStorage.
 *
 * Stores the last N swaps with metadata (tokens, amounts, hash, status,
 * timestamp). The hook reads on mount, writes on every mutation, and
 * periodically re-polls receipts for any entries still marked "pending"
 * — both from previous sessions and from the current session.
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { usePublicClient } from "wagmi";
import type { TokenSymbol } from "@/lib/tokens";

const STORAGE_KEY = "asic-tx-history";
const MAX_ENTRIES = 20;
/** Don't re-poll entries older than 24h — they're likely dropped. */
const STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000;
/** Re-check pending entries every 15 seconds. */
const POLL_INTERVAL_MS = 15_000;

export type TxHistoryStatus = "pending" | "confirmed" | "failed";

export interface TxHistoryEntry {
  hash: string;
  inputToken: TokenSymbol;
  outputToken: TokenSymbol;
  amountIn: string;          // human-readable
  expectedOut: string;       // human-readable
  status: TxHistoryStatus;
  timestamp: number;         // Date.now()
}

function loadHistory(): TxHistoryEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as TxHistoryEntry[];
  } catch {
    return [];
  }
}

function saveHistory(entries: TxHistoryEntry[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(0, MAX_ENTRIES)));
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

export function useTxHistory() {
  const [entries, setEntries] = useState<TxHistoryEntry[]>(loadHistory);
  const publicClient = usePublicClient();
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  // Periodic reconciliation of pending entries
  useEffect(() => {
    if (!publicClient) return;

    let cancelled = false;

    async function reconcile() {
      const current = entriesRef.current;
      const pending = current.filter(
        (e) =>
          e.status === "pending" &&
          Date.now() - e.timestamp < STALE_THRESHOLD_MS,
      );
      if (pending.length === 0) return;

      const updates: Record<string, TxHistoryStatus> = {};

      await Promise.allSettled(
        pending.map(async (entry) => {
          try {
            const receipt = await publicClient!.getTransactionReceipt({
              hash: entry.hash as `0x${string}`,
            });
            updates[entry.hash] =
              receipt.status === "success" ? "confirmed" : "failed";
          } catch {
            // tx not found or RPC error — leave as pending
          }
        }),
      );

      if (cancelled || Object.keys(updates).length === 0) return;

      setEntries((prev) => {
        const next = prev.map((e) =>
          updates[e.hash] ? { ...e, status: updates[e.hash] } : e,
        );
        saveHistory(next);
        return next;
      });
    }

    // Run immediately on mount, then periodically
    void reconcile();
    const timer = setInterval(() => void reconcile(), POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [publicClient]);

  const addTx = useCallback(
    (entry: Omit<TxHistoryEntry, "timestamp">) => {
      setEntries((prev) => {
        const next = [{ ...entry, timestamp: Date.now() }, ...prev].slice(0, MAX_ENTRIES);
        saveHistory(next);
        return next;
      });
    },
    [],
  );

  const updateStatus = useCallback(
    (hash: string, status: TxHistoryStatus) => {
      setEntries((prev) => {
        const next = prev.map((e) =>
          e.hash === hash ? { ...e, status } : e,
        );
        saveHistory(next);
        return next;
      });
    },
    [],
  );

  const clearHistory = useCallback(() => {
    setEntries([]);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  return { entries, addTx, updateStatus, clearHistory };
}
